import store from "@/state/store";

export default [
  {
    path: "/login",
    name: "login",
    component: () => import("../views/pages/account/login"),
    meta: {
      title: "Log in",
      beforeResolve(routeTo, routeFrom, next) {
        // CC360 uses authfack and its HttpOnly token. The legacy `auth` module
        // is only authoritative for the Firebase build; trusting its stale
        // sessionStorage flag here sends a logged-out CC360 user back to home.
        const legacyLoggedIn = process.env.VUE_APP_DEFAULT_AUTH === "firebase"
          && store.getters["auth/loggedIn"];
        const cc360LoggedIn = store.state.authfack
          && store.state.authfack.status
          && store.state.authfack.status.loggeduser;
        if (legacyLoggedIn) {
          const isInternal = localStorage.getItem('is_internal') === 'true';
          next({ name: isInternal ? "ramps" : "home" });
        } else if (cc360LoggedIn && localStorage.getItem('is_internal') !== 'true') {
          next({ name: "home" });
        } else {
          // Internal CC360 sessions stay on /login long enough for the
          // component to verify the HttpOnly token and show the welcome card.
          next();
        }
      }
    }
  },
  {
    path: "/logout",
    name: "logout",
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        const logoutAction = process.env.VUE_APP_DEFAULT_AUTH === "firebase"
          ? store.dispatch("auth/logOut")
          : store.dispatch("authfack/logout");

        Promise.resolve(logoutAction).then(
          () => next({ name: "login" }),
          () => next({ name: "login" }),
        );
      }
    }
  },
  {
    path: "/ramps",
    name: "ramps",
    meta: { title: "Atlas", authRequired: true, internalOnly: true },
    component: () => import("../views/pages/ramps/RampsHub")
  },
  {
    path: "/ramps/glu-kpi",
    name: "ramps_glu_kpi",
    meta: { title: "GLU KPI Dashboard", authRequired: true, internalOnly: true },
    component: () => import("../views/pages/ramps/GluKpiDashboard.vue")
  },
  {
    path: "/ramps/glu/order/:id",
    name: "ramps_glu_order",
    meta: { title: "GLU Order", authRequired: true, internalOnly: true },
    component: () => import("../views/pages/ramps/GluOrderForm.vue")
  },
  {
    path: "/ramps/glu/rhb/:kind(booking|shipment)/:id",
    name: "ramps_glu_rhb",
    meta: { title: "GLU RHB", authRequired: true, internalOnly: true },
    component: () => import("../views/pages/ramps/GluRhbForm.vue")
  },
  {
    path: "/ramps/invoicing",
    name: "ramps_invoicing",
    meta: { title: "Invoicing", authRequired: true, internalOnly: true },
    component: () => import("../views/pages/ramps/InvoicingHub.vue")
  },
  {
    path: "/ramps/invoicing/so-edited-report",
    name: "ramps_invoicing_so_edited_report",
    meta: { title: "Sales Order Edit Report", authRequired: true, internalOnly: true },
    component: () => import("../views/pages/ramps/invoicing/SoEditedReport.vue")
  },
  {
    path: "/ramps/invoicing/ap-ar-dashboard",
    name: "ramps_invoicing_ap_ar_dashboard",
    meta: { title: "Invoicing vs AP vs AR", authRequired: true, internalOnly: true },
    component: () => import("../views/pages/ramps/invoicing/InvoicingApArDashboard.vue")
  },
  {
    path: "/ramps/invoicing/work-order-dashboard",
    name: "ramps_work_order_dashboard",
    meta: { title: "Work Order Dashboard", authRequired: true, internalOnly: true },
    component: () => import("../views/pages/ramps/invoicing/WorkOrderDashboard.vue")
  },
  {
    path: "/ramps/brokerage-kpi",
    name: "ramps_brokerage_kpi",
    meta: { title: "Brokerage KPI Dashboard", authRequired: true, internalOnly: true },
    component: () => import("../views/pages/ramps/BrokerageKpiDashboard.vue")
  },
  {
    path: "/ramps/brokerage/record/:id",
    name: "ramps_brokerage_record",
    meta: { title: "Brokerage Record", authRequired: true, internalOnly: true },
    component: () => import("../views/pages/ramps/BrokerageRecordForm.vue")
  },
  {
    path: "/ramps/reports/ros-delivered",
    name: "ramps_ros_delivered",
    meta: { title: "ROS vs Delivered to Site", authRequired: true, internalOnly: true },
    component: () => import("../views/pages/ramps/RosDeliveredReport.vue")
  },
  {
    path: "/ramps/worker-status",
    name: "ramps_worker_status",
    meta: { title: "Dashboard Worker Status", authRequired: true, internalOnly: true },
    component: () => import("../views/pages/ramps/DashboardWorkerStatus.vue")
  },
  {
    // Standalone portal for the BDE team. The page handles its own login
    // flow (POST /login_odoo) so we deliberately don't set authRequired —
    // unauthenticated visitors land on the embedded sign-in card instead
    // of the cc360 customer login.
    path: "/quote-approval",
    name: "quote_approval",
    meta: { title: "Quote Approval" },
    component: () => import("../views/pages/quote-approval/QuoteApprovalPortal.vue")
  },
  {
    path: "/",
    name: "home",
    meta: { title: "Home", authRequired: true },
    component: () => import("../views/pages/home")
  },
  {
    path: "/home",
    name: "homepage",
    meta: { title: "Home", authRequired: true },
    component: () => import("../views/pages/home")
  },
  {
    path: "/freight-estimator",
    name: "freight_estimator",
    meta: { 
      title: "Freight Cost Estimator", 
      authRequired: true,
      // eslint-disable-next-line no-unused-vars
      beforeResolve(routeTo, routeFrom, next) {
        window.location.href = '/freight/';
      }
    }
  },
  {
    path: "/configure-deposit",
    name: "configure_deposit",
    meta: { title: "Configure Deposit", authRequired: true },
    component: () => import("../views/pages/ConfigureDeposit")
  },
  {
    path: "/urgencies",
    name: "urgencies",
    meta: { title: "Urgencies", authRequired: true },
    component: () => import("../views/pages/urgencies")
  },
  {
    path: "/import-costs",
    name: "Import Costs",
    meta: { title: "Import Costs", authRequired: true },
    component: () => import("../views/pages/brokerage/import-costs")
  },
  {
    path: "/gra-report",
    name: "GRA Report",
    meta: { title: "GRA Report", authRequired: true },
    component: () => import("../views/pages/GRAReport")
  },
  {
    path: "/prc-summary",
    name: "prc_summary",
    meta: { title: "PRC Summary", authRequired: true },
    component: () => import("../views/pages/prc-summary")
  },
  {
    path: "/logistics-spend",
    name: "Logistics Spend",
    meta: { title: "Logistics Spend", authRequired: true },
    component: () => import("../views/pages/LogisticsSpend")
  },
  {
    path: "/po-upload",
    name: "po_upload",
    meta: { title: "PO Upload", authRequired: true },
    component: () => import("../views/pages/POUpload")
  },
  {
    path: "/procurement",
    name: "procurement",
    meta: { title: "Procurement", authRequired: true },
    component: () => import("../views/pages/procurement/table")
  },
  {
    path: "/orders",
    name: "orders",
    meta: { title: "Orders", authRequired: true },
    component: () => import("../views/pages/orders/groupings")
  },
  {
    path: "/purchase_orders",
    name: "purchase_orders",
    meta: { title: "Purchase Orders", authRequired: true },
    component: () => import("../views/pages/purchase_orders/groupings")
  },
  {
    path: "/orders/pending/all/:wh",
    name: "pending",
    meta: { title: "Pending", authRequired: true },
    props: true,
    component: () => import("../views/pages/orders/shipping/groupings")
  },

  {
    path: "/quotations/open",
    name: "quotations",
    meta: {title: "Quotations", authRequired: true},
    props: true,
    component: () => import("../views/pages/quotations/QuotationsView")
  },
  {
    path: "/bookings/:direction/:mode",
    name: "bookings",
    meta: {title: "Bookings", authRequired: true},
    props: true,
    component: () => import("../views/pages/bookings/BookingsView")
  },
  {
    path: "/shipments/imports/air",
    name: "shipments",
    meta: {title: "Shipments", authRequired: true},
    props: true,
    component: () => import("../views/pages/shipments/ShipmentsView")
  }, 
  {
    path: "/transit/imports/air",
    name: "transit",
    meta: {title: "In Transit", authRequired: true},
    props: true,
    component: () => import("../views/pages/shipments/TransitView")
  }, 
  {
    path: "/brokerage/imports/ocean",
    name: "brokerage",
    meta: {title: "Brokerage", authRequired: true},
    props: true,
    component: () => import("../views/pages/brokerage/BrokerageView")
  }, 
  {
    path: "/completed/imports/ocean",
    name: "completed",
    meta: {title: "Completed", authRequired: true},
    props: true,
    component: () => import("../views/pages/brokerage/CompletedView")
  }, 
  {
    path: "/maximo/imports/ocean",
    name: "atlantic",
    meta: {title: "Atlantic Warehouse", authRequired: true},
    props: true,
    component: () => import("../views/pages/maximo/ShipmentsView")
  }, 
  {
    path: "/overview",
    name: "overview",
    meta: { title: "Overview", authRequired: true },
    component: () => import("../views/pages/overview/OverviewView")
  },
  {
    path: "/invoicing",
    name: "invoices",
    meta: {title: "Invoicing", authRequired: true},
    props: true,
    component: () => import("../views/pages/invoicing/InvoiceView")
  },
  {
    path: "/account/login",
    // Must NOT reuse the name "login": vue-router 4 removes an existing route
    // when a new one registers with the same name, so this entry used to
    // delete the /login route (registered earlier) from the matcher. That
    // made every push to /login unmatched (guard bounced to /) and broke
    // logout + fresh/incognito visits.
    name: "account-login",
    meta: { title: "Login", authRequired: true },
    component: () => import("../views/pages/account/login")
  }
]
